import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InternalChatbotService } from '../../../core/services/internal-chatbot.service';

/**
 * Documents History — internal chatbot document training operations.
 *
 * Processing starts immediately on upload (no scheduler): rows go
 * PROCESSING → COMPLETED | FAILED. There is no cancel (no pending window);
 * FAILED uploads show Retry, which re-runs processing on the already-stored
 * file (no re-upload needed).
 */
interface DocHistoryRow {
  id: number;
  operation: string; // ADD / DELETE
  documentId: number | null;
  documentName: string;
  status: string; // PROCESSING / COMPLETED / FAILED
  createdBy: number | null;
  createdByName: string;
  createdAt: string;
  startedAt: string;
  completedAt: string;
  details: string;
  retryCount: number;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

@Component({
  selector: 'app-upload-documents-history',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './upload-documents-history.component.html',
  styleUrl: './upload-documents-history.component.css'
})
export class UploadDocumentsHistoryComponent implements OnInit, OnDestroy {

  data: DocHistoryRow[] = [];
  loading = true;
  page = 1;
  rowsPerPage = 10;
  totalPages = 1;
  totalRows = 0;
  toasts: Toast[] = [];
  skeletonRows = Array(8);

  // Server-side filters
  statusOptions = ['PROCESSING', 'COMPLETED', 'FAILED'];
  filterStatus = '';
  filterDateFrom = '';
  filterDateTo = '';
  filterUser = '';

  private pollHandle: any = null;
  private toastIdCounter = 0;

  constructor(private internalChatbotService: InternalChatbotService) { }

  ngOnInit(): void {
    this.fetchData();
    // Auto-refresh every 30 seconds (silent — no skeleton flash)
    this.pollHandle = setInterval(() => this.fetchData(true), 30000);
  }

  ngOnDestroy(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
    }
  }

  // ─── Fetch (server-side pagination + filters) ───
  fetchData(silent = false): void {
    if (!silent) {
      this.loading = true;
    }

    const user = this.filterUser.trim();
    this.internalChatbotService.getDocTrainingHistory({
      page: this.page - 1,
      size: this.rowsPerPage,
      status: this.filterStatus || undefined,
      dateFrom: this.filterDateFrom || undefined,
      dateTo: this.filterDateTo || undefined,
      createdBy: user && !isNaN(Number(user)) ? user : undefined
    }).subscribe({
      next: (json) => {
        this.data = json?.data || [];
        this.totalRows = json?.totalRows ?? this.data.length;
        this.totalPages = Math.max(1, json?.totalPages ?? 1);
        this.loading = false;
      },
      error: () => {
        if (!silent) {
          this.addToast('Failed to load documents history', 'error');
          this.data = [];
        }
        this.loading = false;
      }
    });
  }

  onFilterChange(): void {
    this.page = 1;
    this.fetchData();
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterDateFrom = '';
    this.filterDateTo = '';
    this.filterUser = '';
    this.page = 1;
    this.fetchData();
  }

  hasFilters(): boolean {
    return !!(this.filterStatus || this.filterDateFrom || this.filterDateTo || this.filterUser);
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages) return;
    this.page = p;
    this.fetchData();
  }

  onPageSizeChange(): void {
    this.rowsPerPage = Number(this.rowsPerPage);
    this.page = 1;
    this.fetchData();
  }

  // ─── Retry (FAILED uploads only — document bytes are already stored) ───
  retryTask(row: DocHistoryRow): void {
    this.internalChatbotService.retryDocTask(row.id).subscribe({
      next: () => {
        this.addToast('Retry started');
        this.fetchData(true);
      },
      error: (err) => {
        const msg = err?.error?.message || 'Failed to retry';
        this.addToast(msg, 'error');
        this.fetchData(true);
      }
    });
  }

  canRetry(row: DocHistoryRow): boolean {
    return row.operation === 'ADD' && row.status === 'FAILED';
  }

  // ─── Helpers ───
  getRowClass(idx: number): string {
    return idx % 2 === 0 ? 'qu-tr-even' : 'qu-tr-odd';
  }

  getStatusClass(status: string): string {
    const s = (status || '').toUpperCase();
    if (s === 'COMPLETED') return 'st-completed';
    if (s === 'PROCESSING') return 'st-progress';
    if (s === 'FAILED') return 'st-failed';
    return '';
  }

  statusLabel(status: string): string {
    return status === 'PROCESSING' ? 'IN PROGRESS' : status;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  addToast(message: string, type: 'success' | 'error' = 'success'): void {
    const id = ++this.toastIdCounter;
    this.toasts.push({ id, message, type });
    setTimeout(() => {
      this.toasts = this.toasts.filter(t => t.id !== id);
    }, 3000);
  }
}
